import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, ShieldCheck, ClipboardList, Building2, FileText, User, LogIn, Star, KanbanSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import BrandLogo from "@/brand/BrandLogo";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ADMIN_ROLES = ["super_admin", "admin"];
const OPERATOR_ROLES = ["super_admin", "admin", "agency"];

export default function Navbar() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const isAdmin = user && ADMIN_ROLES.includes(user.role);
  const isOperator = user && OPERATOR_ROLES.includes(user.role);

  const [reviewCount, setReviewCount] = useState(0);
  useEffect(() => {
    if (!isAdmin) { setReviewCount(0); return; }
    let alive = true;
    const load = () => api.get("/admin/review-queue/count")
      .then(({ data }) => { if (alive) setReviewCount(data?.count || 0); })
      .catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [isAdmin, pathname]);

  if (pathname === "/scan") return null;
  // Client-facing share-link pages carry their own minimal header — no internal app nav.
  if (pathname.startsWith("/p/")) return null;

  const linkCls = "inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors";

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2.5 group min-w-0" data-testid="nav-logo">
          <BrandLogo
            variant="wordmark"
            imgClass="h-8 sm:h-9 w-auto object-contain shrink-0 transition-transform duration-200 group-hover:scale-[1.03]"
            markClass="w-9 h-9 shrink-0 transition-transform duration-200 group-hover:rotate-6"
            textClass="max-[400px]:hidden text-[17px]"
          />
        </Link>

        <nav className="flex items-center gap-0.5 sm:gap-2 shrink-0">
          <Link to="/dashboard" data-testid="nav-dashboard" className={linkCls}>
            <LayoutDashboard className="w-4 h-4" /> <span className="hidden md:inline">Dashboard</span>
          </Link>
          <Link to="/businesses" data-testid="nav-businesses" className={linkCls}>
            <Building2 className="w-4 h-4" /> <span className="hidden md:inline">My Businesses</span>
          </Link>
          <Link to="/tasks" data-testid="nav-tasks" className={linkCls}>
            <ClipboardList className="w-4 h-4" /> <span className="hidden md:inline">Fix Plan</span>
          </Link>
          <Link to="/reputation" data-testid="nav-reputation" className={linkCls}>
            <Star className="w-4 h-4" /> <span className="hidden md:inline">Reputation</span>
          </Link>

          {user ? (
            <>
              <Link to="/proposals" data-testid="nav-proposals" className={linkCls}>
                <FileText className="w-4 h-4" /> <span className="hidden md:inline">Proposals</span>
              </Link>
              {isOperator && (
                <Link to="/pipeline" data-testid="nav-pipeline" className={linkCls}>
                  <KanbanSquare className="w-4 h-4" /> <span className="hidden md:inline">Pipeline</span>
                </Link>
              )}
              {isAdmin && (
                <Link to="/admin" data-testid="nav-admin" className={`${linkCls} relative`}>
                  <ShieldCheck className="w-4 h-4" /> <span className="hidden md:inline">Admin</span>
                  {reviewCount > 0 && (
                    <span data-testid="nav-admin-review-badge"
                      className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-amber-500 text-white text-[10px] font-bold px-1 leading-none">
                      {reviewCount > 99 ? "99+" : reviewCount}
                    </span>
                  )}
                </Link>
              )}
              <Link to="/account" data-testid="nav-account" className={linkCls}>
                <User className="w-4 h-4" /> <span className="hidden md:inline">Account</span>
              </Link>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login" className={linkCls}>
              <LogIn className="w-4 h-4" /> <span className="hidden sm:inline">Sign in</span>
            </Link>
          )}

          <Button
            data-testid="nav-start-scan"
            onClick={() => nav("/scan")}
            className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-medium px-3 sm:px-5 shrink-0 active:scale-95 transition-transform">
            <span className="sm:hidden">Scan</span>
            <span className="hidden sm:inline">Start Free Scan</span>
          </Button>
        </nav>
      </div>
    </header>
  );
}
